import { Injectable, Inject, Injector } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClient, HttpHeaders, HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';

import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Observable, Subject, interval, throwError } from 'rxjs';
import { filter, takeUntil, map, catchError, takeWhile } from 'rxjs/operators';
import { BehaviorSubject, SubscriptionLike as ISubscription } from 'rxjs';
import _ from 'lodash';

import { Configuration } from '../../app.configuration.service';
import { environment } from './../../../environments/environment';
import { ApplicationError } from '../model/generic/applicationError';

export class UserInfo {
  public username: string;
  public name: string;
  public accessToken: string;
  public entityId: number;
  public centroCustoId: number;
  public permissions: any;
  public azureAD: boolean;
}

export class UserExpireInfo {
  public timeout: number;
  public totalTimeout: number;

  constructor(timeout: number, totalTimeout: number) {
    this.timeout = timeout;
    this.totalTimeout = totalTimeout;
  }
}

@Injectable()
export class SecurityService {

  public returnUrl: string;

  private userInfo = new BehaviorSubject<UserInfo>(null);
  public userInfo$ = this.userInfo.asObservable();

  private userExpiring = new BehaviorSubject<UserExpireInfo>(null);
  public userExpiring$ = this.userExpiring.asObservable();

  private config: Configuration;
  private handlerExpirationTimer = null;
  private lastServerAccess: Date = new Date();
  private monitoringExpiration: boolean;
  public isCTT: boolean | false;

  private tokenEndpoint: string;
  private contentType: string;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private http: HttpClient, private router: Router, private configuration: Configuration,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService, private msalBroadcastService: MsalBroadcastService) {

    // parametros
    this.config = configuration;

    const hostname = this.document.location.hostname;
    this.isCTT = hostname == 'ctt.boxer.pt' || hostname == 'ctt-qa.boxer.pt' || (!environment.production && environment.isCTT); // || hostname == 'localhost';

    if (this.config.isIdentityServer) {
      this.contentType = 'application/x-www-form-urlencoded';
      this.tokenEndpoint = 'connect/token';
    } else {
      this.contentType = 'application/json';
      this.tokenEndpoint = 'token';
    }

    if (this.isCTT) {
      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None),
          takeUntil(this._destroying$)
        )
        .subscribe(() => {
          this.setAzureInfo();
        });

      this.authService
        .handleRedirectObservable()
        .subscribe(response => {
          if (response != null && response.account != null) {
            const data = response.account;
            this.authService.instance.setActiveAccount(data);

            setTimeout(() => {
              this
                .loginWithToken(data.tenantId, data.idTokenClaims.aud, data.username, response.accessToken)
                .subscribe((sucess: any) => {
                  if (sucess) {
                    this.router.navigate(['home']);
                    // if (this.returnUrl) {
                    //   this.router.navigate([this.returnUrl]);
                    // } else {
                    // }
                  }
                },
                  (error: any) => {
                    console.log(error);
                  });
            }, 1000);

            return true;
          }
        });
    }

    // check if is a page reload (sessionStorage contains currentUser)
    const data = JSON.parse(sessionStorage.getItem('currentUser'));
    if (data) {  // page reload, ajusta timer de expiração
      if (environment.production !== false) {
        console.log('SecurityService:: page reload, ajusta timer de expiração');
      }
      this.setMonitoringExpiration(true);
      this.setExpirationHandler(data);
    }
  }

  setAzureInfo = (): void => {
    const account = this.authService?.instance?.getActiveAccount();
    if (account !== null) {
      sessionStorage.setItem('azureInfo', JSON.stringify(account));
    }
  }

  public GetUserInfo = (): UserInfo => {
    const data = JSON.parse(sessionStorage.getItem('currentUser'));

    if (data) {
      const uInfo = new UserInfo();
      uInfo.username = (data as any).username;
      uInfo.name = (data as any).name;
      uInfo.accessToken = (data as any).access_token;
      uInfo.entityId = (data as any).entityId;
      uInfo.centroCustoId = (data as any).centroCustoId;
      uInfo.permissions = (data as any).permissions;
      uInfo.azureAD = (data as any).azureAD;
      return uInfo;
    } else {
      return null;
    }
  }

  public UserInfoSubscribe = (func: any): ISubscription => {
    const sub = this.userInfo$.subscribe(func);

    // one subscriber => load the data, if not loaded and not requested
    this.userInfo.next(this.GetUserInfo());

    return sub;
  }

  public UserExpiringSubscribe = (func: any): ISubscription => {
    const sub = this.userExpiring$.subscribe(func);
    console.log('SecurityService:: UserExpiringSubscribe');
    return sub;
  }

  public loginAzureAD() {
    this.setExpirationHandler(null);

    return this.authService
      .loginRedirect()
      .pipe(
        map((response: any) => {
          return true;
        })
      );
  }

  public loginWithToken(tennantId: string, clientId: string, username: string, token: string) {
    this.setExpirationHandler(null);

    const fullUrl = `${this.config.apiServerUrl}/${this.tokenEndpoint}`;
    const myHeaders = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set('Accept', 'application/json');

    let body = `application=FrontOffice&tennantId=${tennantId}&clientId=${clientId}`;
    body += `&username=${username}&password=${'xPTo'}`;
    if (this.config.isIdentityServer) {
      body += `&client_id=vGrafWebApi&client_secret=vGrafWebApi..2019&scope=vGrafWebApi`;
    }
    body += "&grant_type=password";

    return this.http
      .post(fullUrl, body, { headers: myHeaders })
      .pipe(
        map((response: any) => {
          const data = response;
          if (data && (data as any).access_token) {
            (data as any).azureAD = true;

            // calculate expire date
            const expireDate = new Date();
            expireDate.setSeconds(expireDate.getSeconds() + (data as any).expires_in);
            (data as any).expireDate = expireDate.getTime();
            (data as any).username = username;
            sessionStorage.setItem('currentUser', JSON.stringify(data));

            // set timeout to expiration date of token
            this.setMonitoringExpiration(true);
            this.setExpirationHandler(data);
            this.userInfo.next(this.GetUserInfo());
            return true;
          } else {
            this.userInfo.next(this.GetUserInfo());
            return false;
          }
        }), catchError(this.handleError));
  }

  public login(username: string, password: string) {
    this.setExpirationHandler(null);

    const fullUrl = `${this.config.apiServerUrl}/${this.tokenEndpoint}`;
    const myHeaders = new HttpHeaders()
      .set('Content-Type', this.contentType)
      .set('Accept', 'application/json');

    let body = 'grant_type=password&application=FrontOffice';
    body += `&username=${username}&password=${encodeURIComponent(password)}`;
    if (this.config.isIdentityServer) {
      body += `&client_id=vGrafWebApi&client_secret=vGrafWebApi..2019&scope=vGrafWebApi`;
    }

    return this.http
      .post(fullUrl, body, { headers: myHeaders })
      .pipe(
        map((response: any) => {
          const data = response;
          if (data && (data as any).access_token) {
            (data as any).azureAD = false;

            // calculate expire date
            const expireDate = new Date();
            expireDate.setSeconds(expireDate.getSeconds() + (data as any).expires_in);
            (data as any).expireDate = expireDate.getTime();
            (data as any).username = username;
            sessionStorage.setItem('currentUser', JSON.stringify(data));

            // set timeout to expiration date of token
            this.setMonitoringExpiration(true);
            this.setExpirationHandler(data);
            this.userInfo.next(this.GetUserInfo());
            return true;
          } else {
            this.userInfo.next(this.GetUserInfo());
            return false;
          }
        }), catchError(this.handleError));
  }

  private setExpirationHandler = (data: any) => {
    if (data) {
      const aux =
        interval(1000).pipe(
          takeWhile(() => {
            const timer = data.expireDate - (new Date().getTime());
            return this.monitoringExpiration && (this.handlerExpirationTimer != null && timer >= 0);
          }));

      this.handlerExpirationTimer = aux.subscribe(() => {
        // calculte expire interval in miliseconds
        const tokenExpiresIn = data.expireDate - (new Date().getTime());

        // calcula sliding expiration date
        const slidingExpirationTimeout =
          (this.lastServerAccess.getTime() + this.configuration.authenticationSlidingExpiration * 1000 - (new Date()).getTime()) / 1000;

        // console.log('SS: monitoringExpiration:' + this.monitoringExpiration +
        //   ', Token expires in: ' + tokenExpiresIn / 1000 +
        //   ', lastServerAccess:' + this.lastServerAccess +
        //   ', slidingExpirationDate: ' + slidingExpirationTimeout);

        // validate if is time to automatic renovation (sliding expiration)
        if (tokenExpiresIn / 1000 < 30 && slidingExpirationTimeout > 30) {
          this.refresh()
            .subscribe((sucess: any) => {
              if (sucess) {
              }
            },
              (error: any) => {
                console.log(error);
              });
        } else {
          this.userExpiring.next(
            new UserExpireInfo(slidingExpirationTimeout * 1000, this.configuration.authenticationSlidingExpiration * 1000)
          );
        }
      });
    } else {
      if (this.handlerExpirationTimer != null) {
        this.handlerExpirationTimer.unsubscribe();
        this.handlerExpirationTimer = null;
      }
    }
  }

  public setMonitoringExpiration = (action: boolean) => {
    this.monitoringExpiration = action;
  }

  public refresh() {
    console.log('SecurityService: refresh token ');
    this.setExpirationHandler(null);

    // grant_type: 'refresh_token',
    // refresh_token: refresh_token,
    // username: this.authorizationData.userName,
    // access_token: this.authorizationData.access_token })
    const data = JSON.parse(sessionStorage.getItem('currentUser'));

    if (data) {
      const fullUrl = `${this.config.apiServerUrl}/${this.tokenEndpoint}`;
      const myHeaders = new HttpHeaders()
        .set('Authorization', 'Bearer ' + data.access_token)
        .set('Content-Type', this.contentType)
        .set('Accept', 'application/json')
        .set('username', data.username);

      let body = 'grant_type=refresh_token&application=FrontOffice';
      body += `refresh_token=${data.refresh_token}&username=${data.username}&access_token=${data.access_token}`;
      if (this.config.isIdentityServer) {
        body += `&client_id=vGrafWebApi&client_secret=vGrafWebApi..2019&scope=vGrafWebApi`;
      }

      return this.http
        .post(fullUrl, body, { headers: myHeaders })
        .pipe(
          map(response => {
            const authData = response;
            if (authData && (authData as any).access_token) {
              // calculate expire date
              const expireDate = new Date();
              expireDate.setSeconds(expireDate.getSeconds() + (authData as any).expires_in);
              (authData as any).expireDate = expireDate.getTime();
              sessionStorage.setItem('currentUser', JSON.stringify(authData));

              // set timeout to expiration date of token
              this.setMonitoringExpiration(true);
              this.setExpirationHandler(authData);
              return true;
            } else {
              return false;
            }
          }), catchError(this.handleError)
        );
    }
  }

  public logout(gotoLogin: boolean, gotoHome: boolean) {
    // remove user from local storage to log user out
    sessionStorage.removeItem('currentUser');

    if (this.authService?.instance?.getActiveAccount() !== null) {
      const hostname = this.document.location.origin;
      this.authService.instance.setActiveAccount(null);
      this.authService.logout({
        postLogoutRedirectUri: hostname
      });
    }

    this.userInfo.next(null);
    this.userExpiring.next(null);

    this.setExpirationHandler(null);
    this.setMonitoringExpiration(false);

    if (gotoLogin) {
      this.returnUrl = '/';
      this.router.navigate(['/login']);
    }
    if (gotoHome) { this.router.navigate(['/']); }
  }

  public alive = () => {
    // console.log('SecuritySevice ALIVE !!!!!!!!!!! ALIVE AND KICKING');
    this.lastServerAccess = new Date();
  }

  // http://jasonwatmore.com/post/2016/09/29/angular-2-user-registration-and-login-example-tutorial
  // http://jasonwatmore.com/post/2016/12/08/angular-2-redirect-to-previous-url-after-login-with-auth-guard

  public canActivate = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean => {
    if (state.url === '/changepassword') {
      return true;
    }

    // if (environment.production == false) {
    //   if (route) {
    //     console.log('canActivate:' + JSON.stringify(route.data));
    //   }
    // }

    if (route && route.data && route.data.navigationName) {
      const data = sessionStorage.getItem('currentUser');
      if (!data) {
        // // not logged in so redirect to login page with the return url
        this.returnUrl = state.url;
        this.router.navigate(['/login']);
        return false;
      }

      // TODO: há user, validar que tem permissões para esta opção
      // permissão da rota .... route.data.navigationName, deve mapear com
      const permissions = JSON.parse(data).permissions;
      const element = _.find(permissions, ['LockId', route.data.lockId]);

      return element && element.MyPermission === 2;
    } else {
      // // not logged in so redirect to login page with the return url
      this.returnUrl = state.url;
      this.router.navigate(['/login']);
      return false;
    }
  }

  public canDisplay = (lockId: number): boolean => {
    const user = sessionStorage.getItem('currentUser');
    if (!user) {
      this.router.navigate(['/login']);
      return false;
    } else {
      const permissions = JSON.parse(user).permissions;
      const element = _.find(permissions, ['LockId', lockId]);
      return element && element.MyPermission === 2;
    }
  }

  protected handleError(err: any) {
    let errMsg: string;

    if (err instanceof ApplicationError) {
      return throwError(err);
    }

    try {
      const errorObject = JSON.parse(err._body);
      if (errorObject) {
        if (errorObject.error) {
          errMsg = errorObject.error + ' - ' + errorObject.error_description;
        } else if (errorObject.exceptionMessage) {
          errMsg = errorObject.exceptionMessage;
        }
      }
      return throwError(errMsg);
    } catch (error) {
    }

    if (err.error instanceof Error) {
      errMsg = 'An error occurred:' + err.error.message;
    } else {
      errMsg = `Backend returned code ${err.status}, body was: ${err.error}`;
    }

    console.error(errMsg);
    return throwError(errMsg);
  }
}

@Injectable()
export class CanActivateNavigation {
  constructor(private securityService: SecurityService) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    return this.securityService.canActivate(route, state);
  }
}

@Injectable()
export class RestInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // add authorization header with jwt token if available => possible todo
    // console.log('RestInterceptor');
    const securityService = this.injector.get(SecurityService); // to avoid circular dependencys
    securityService.alive();

    return next.handle(request)
      .pipe(
        catchError((err: HttpErrorResponse) => {

          if (err.error instanceof Error) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', err.error.message);
          } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,

            // erro de autenticação
            if (err.error.error === 'Falha de Autenticação') {
              const errApp = new ApplicationError(err.error.error_description);
              errApp.systemError = false;
              return throwError(errApp as ApplicationError);
            }

            if (err.error.exceptionType) {
              if (err.error.exceptionType === 'System.ApplicationException') {
                // application error message
                const errApp = new ApplicationError(err.error.exceptionMessage);
                errApp.systemError = false;
                return throwError(errApp as ApplicationError);
              }
              if (err.error.exceptionType.lastIndexOf('System.', 0) === 0) {
                // server side exceptions
                const errApp = new ApplicationError(err.error.exceptionMessage);
                errApp.systemError = true;
                return throwError(errApp as ApplicationError);
              }
            } else {
              // server side exceptions
              const errApp = new ApplicationError(err.message);
              errApp.systemError = true;
              return throwError(errApp as ApplicationError);
            }

            console.error(`Backend x returned code ${err.status}, body was: ${err.error}`);
          }

          switch (err.status) {
            case 400:
              break;
            case 401:
              break;
            default:
              break;
          }

          //     // ...optionally return a default fallback value so app can continue (pick one)
          //     // which could be a default value (which has to be a HttpResponse here)
          //     // return Observable.of(new HttpResponse({body: [{name: "Default value..."}]}));
          //     // or simply an empty observable
          //     return Observable.empty<HttpEvent<any>>();

          // const appError = new Error('');
          // appError.

          return throwError(err);
        })
      );

  }
}
